import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { useCostCenters, CostCenterFormData, CostCenterTableRow } from "@/hooks/useCostCenters";
import { SimpleCostCenterFilters, useSimpleCostCenterFilters } from "@/components/cost-centers/SimpleCostCenterFilters";
import { formatCostCenterCode } from "@/types/cost-center";

// Define the schema for cost center form
const formSchema = z.object({
  code: z.number()
    .int("O código deve ser um número inteiro")
    .min(0, "O código deve ser maior ou igual a 0")
    .max(9999, "O código deve ser menor ou igual a 9999")
    .nullable()
    .optional(),
  name: z.string().min(2, "O nome deve ter pelo menos 2 caracteres"),
  description: z.string().optional(),
});

// Component for adding or editing a cost center
export function CostCenterForm({ onSuccess, costCenter }: {
  onSuccess: (data?: any) => void;
  costCenter?: CostCenterTableRow;
}) {
  const { createCostCenter, updateCostCenter, isCreating, isUpdating } = useCostCenters();
  const isEditing = !!costCenter;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: costCenter?.code ?? null,
      name: costCenter?.name || "",
      description: costCenter?.description || "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      let result;
      if (isEditing && costCenter) {
        result = await updateCostCenter(costCenter.id, values);
      } else {
        const { data: user } = await supabase.auth.getUser();
        if (!user || !user.user) {
          throw new Error("Usuário não autenticado");
        }
        result = await createCostCenter({ formData: values, userId: user.user.id });
      }

      form.reset();
      onSuccess(result); // Pass the created/updated cost center data
    } catch (error) {
      console.error("Erro ao salvar centro de custo:", error);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="code"
          render={({ field: { onChange, value, ...field } }) => (
            <FormItem>
              <FormLabel>Código (4 dígitos)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  min="0"
                  max="9999"
                  placeholder="0-9999 (opcional)"
                  value={value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      onChange(null);
                    } else {
                      const num = parseInt(val, 10);
                      if (!isNaN(num)) {
                        onChange(num);
                      }
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Nome do centro de custo" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Descrição (opcional)" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" disabled={isCreating || isUpdating}>
          {isCreating || isUpdating ? "Salvando..." : "Salvar"}
        </Button>
      </form>
    </Form>
  );
}

// Create table columns
export const costCenterColumns: ColumnDef<CostCenterTableRow>[] = [
  {
    accessorKey: "code",
    header: "Código",
    cell: ({ row }) => formatCostCenterCode(row.original.code) || "-",
  },
  {
    accessorKey: "name",
    header: "Nome",
  },
  {
    accessorKey: "description",
    header: "Descrição",
  },
  {
    id: "actions",
    header: "Ações",
    cell: ({ row }) => row.original.renderActions?.(),
    enableSorting: false,
    enableHiding: false,
  },
];

// Component for editing a cost center
function EditCostCenterDialog({ costCenter, onSuccess, open, setOpen }: {
  costCenter: CostCenterTableRow;
  onSuccess: () => void;
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Centro de Custo</DialogTitle>
          <DialogDescription>
            Edite as informações do centro de custo selecionado.
          </DialogDescription>
        </DialogHeader>
        <CostCenterForm
          costCenter={costCenter}
          onSuccess={() => {
            setOpen(false);
            onSuccess();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// Component for deleting a cost center
function DeleteCostCenterDialog({ costCenter, onSuccess, open, setOpen }: {
  costCenter: CostCenterTableRow;
  onSuccess: () => void;
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  const { deleteCostCenter, isDeleting } = useCostCenters();

  async function handleDelete() {
    try {
      await deleteCostCenter(costCenter.id);
      setOpen(false);
      onSuccess();
    } catch (error) {
      console.error("Erro ao excluir centro de custo:", error);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Excluir Centro de Custo</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir o centro de custo <b>{costCenter.name}</b>?
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? "Excluindo..." : "Excluir"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Main component that combines the table and form
export function CostCenterManager() {
  const [open, setOpen] = useState(false);
  const [editDialog, setEditDialog] = useState<{ open: boolean; costCenter: CostCenterTableRow | null }>({ open: false, costCenter: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; costCenter: CostCenterTableRow | null }>({ open: false, costCenter: null });

  const { costCenters, isLoading } = useCostCenters();
  const { filteredCostCenters, setFilters, hasActiveFilters } = useSimpleCostCenterFilters(costCenters);

  // Adiciona métodos de ação a cada cost center filtrado
  const dataWithActions = filteredCostCenters.map((cc) => ({
    ...cc,
    renderActions: () => (
      <div className="flex gap-2">
        <Button size="icon" variant="ghost" onClick={() => setEditDialog({ open: true, costCenter: cc })} title="Editar">
          <Pencil className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" onClick={() => setDeleteDialog({ open: true, costCenter: cc })} title="Excluir">
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>
    ),
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Centros de Custo</h2>
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground">
              Mostrando {filteredCostCenters.length} de {costCenters.length} centros
            </p>
          )}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Novo Centro
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Centro de Custo</DialogTitle>
              <DialogDescription>
                Preencha as informações para criar um novo centro de custo.
              </DialogDescription>
            </DialogHeader>
            <CostCenterForm onSuccess={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <SimpleCostCenterFilters
            onFiltersChange={setFilters}
            className="sticky top-4"
          />
        </div>

        <div className="lg:col-span-3">
          <DataTable
            columns={costCenterColumns}
            data={dataWithActions}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Dialogs de edição e exclusão */}
      {editDialog.costCenter && (
        <EditCostCenterDialog
          costCenter={editDialog.costCenter}
          open={editDialog.open}
          setOpen={(v) => setEditDialog({ open: v, costCenter: v ? editDialog.costCenter : null })}
          onSuccess={() => setEditDialog({ open: false, costCenter: null })}
        />
      )}
      {deleteDialog.costCenter && (
        <DeleteCostCenterDialog
          costCenter={deleteDialog.costCenter}
          open={deleteDialog.open}
          setOpen={(v) => setDeleteDialog({ open: v, costCenter: v ? deleteDialog.costCenter : null })}
          onSuccess={() => setDeleteDialog({ open: false, costCenter: null })}
        />
      )}
    </div>
  );
}
